import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * A dónde habla la aplicación.
 *
 * En producción es `/api` del mismo dominio: nginx recibe esa ruta y la pasa
 * al backend por la red interna del servidor. Así el único dominio que existe
 * para quien mire el código del navegador es el de la aplicación —no se ve el
 * servidor, ni el proveedor, ni la dirección del backend.
 *
 * No puede ser una dirección interna del servidor: esta aplicación se ejecuta
 * en el navegador del cliente, a kilómetros de ahí, y un nombre como
 * `http://kong:8000` no significa nada fuera del servidor. Lo que sí se logra
 * es que la dirección pública sea la misma que la de la aplicación.
 *
 * VITE_SUPABASE_URL sigue teniendo prioridad cuando está definida: en
 * desarrollo se apunta directo al backend, y deja una salida si alguna vez
 * hace falta separarlos.
 */
const url = import.meta.env.VITE_SUPABASE_URL || `${window.location.origin}/api`;

/**
 * La misma dirección, para quien tenga que llamar a una Edge Function a mano.
 *
 * Existe porque la pantalla de suscripción leía `VITE_SUPABASE_URL` por su
 * cuenta. Esa variable está vacía en producción a propósito —se habla por
 * `/api` del mismo dominio—, así que la petición salía a
 * `https://grooming-erp.com/functions/v1/...` y nginx respondía 405. El pago
 * no llegaba a intentarse.
 *
 * Quien necesite la URL la toma de aquí y no del entorno.
 */
export const supabaseUrl = url;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!anon) {
  throw new Error('Falta VITE_SUPABASE_ANON_KEY. Defínela en .env');
}

/**
 * Supabase client — untyped at the Database level.
 * The type safety layer lives in `src/lib/mappers.ts` + `src/types/database.ts`,
 * which translates snake_case rows → camelCase DTOs consumed by the UI.
 * Using the generic Database type with supabase-js v2 is brittle and tends to
 * infer `never` for complex relations; the mapper pattern is more robust.
 */
export const supabase: SupabaseClient = createClient(url, anon, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'groomingpro-auth',
  },
  db: { schema: 'public' },
  realtime: { params: { eventsPerSecond: 5 } },
});
